<template>
  <v-container fluid class="pa-6 pa-md-8">
    <div class="page-head d-flex flex-wrap align-center justify-space-between">
      <div>
        <h1 class="page-head__title">Balances</h1>
        <p class="page-head__sub">Member account balances by product.</p>
      </div>
      <v-btn
        color="primary"
        depressed
        class="text-none add-btn"
        @click="open({ idx: 'add', status: true })"
      >
        <v-icon small left>mdi-tray-arrow-up</v-icon>
        Upload Balances
      </v-btn>
    </div>

    <div class="toolbar d-flex align-center mb-4">
      <v-text-field
        outlined
        dense
        rounded
        hide-details
        prepend-inner-icon="mdi-magnify"
        placeholder="Search by account number…"
        v-model="search"
        color="primary"
        class="toolbar__search"
      />
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            class="ml-2 toolbar__refresh"
            :loading="pageLoading"
            @click="$store.dispatch('balances/list', '?page=1&size=30')"
            v-on="on"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
        <span>Get latest balances</span>
      </v-tooltip>
    </div>

    <v-data-table
      :headers="headers"
      :items="balances"
      :loading="pageLoading"
      item-key="branch"
      :server-items-length="paginate.total"
      :options.sync="options"
      class="data-table"
      loading-text="Loading… Please wait"
      :footer-props="{ itemsPerPageOptions: [30, 40, 50] }"
    >
      <template #item.customerId="{ item }">
        <span v-if="item.customerId" class="cell-strong text-capitalize"
          >{{ item.customerId.firstName ? item.customerId.firstName : "" }}
          {{ item.customerId.lastName ? item.customerId.lastName : "" }}</span
        >
      </template>
      <template #item.accountNumber="{ item }">
        <span class="tabular">{{ item.accountNumber }}</span>
      </template>
      <template #item.shares="{ item }">
        <span class="tabular">{{ item.shares | currencyFormat }}</span>
      </template>
      <template #item.susu="{ item }">
        <span class="tabular">{{ item.susu | currencyFormat }}</span>
      </template>
      <template #item.kidsKitty="{ item }">
        <span class="tabular">{{ item.kidsKitty | currencyFormat }}</span>
      </template>
      <template #item.specialDeposit="{ item }">
        <span class="tabular">{{ item.specialDeposit | currencyFormat }}</span>
      </template>
      <template #item.loans="{ item }">
        <span class="tabular">{{ item.loans | currencyFormat }}</span>
      </template>
      <template #item.savings="{ item }">
        <span class="tabular">{{ item.savings | currencyFormat }}</span>
      </template>
    </v-data-table>

    <BulkUploadDialog
      :state="dialog"
      @actions="open"
      @uploadFile="uploadFile"
      :loading="isLoading"
    />
    <SnackBar
      :showSnackBar="showSnackBar"
      :message="message"
      :status="status"
    />
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { debounce } from "lodash";
import SnackBarMIxins from "@/mixins/SnackBarMIxins";
import { loadComponent } from "@/utils/helpers";
export default {
  name: "Transactions",
  components: {
    BulkUploadDialog: () => import(`../../../component/BulkUploadDialog`),
    SnackBar: loadComponent("component/SnackBar")
  },
  data() {
    return {
      search: "",
      options: {
        page: 1,
        itemsPerPage: 30
      },
      menu: false,
      dates: [],
      chosenDateRange: []
    };
  },
  mixins: [SnackBarMIxins],
  filters: {
    currencyFormat(value) {
      return new Intl.NumberFormat("en-GB", {
        currency: "GHS",
        style: "currency",
        minimumFractionDigits: 2
      }).format(value);
    }
  },

  computed: {
    ...mapGetters({
      pageLoading: "getIsPageLoading",
      paginate: "balances/getPagination",
      balances: "balances/getAllBalances",
      isLoading: "getIsLoading"
    }),
    dateRangeText() {
      return this.dates.join(" ~ ");
    },
    transactionsList() {
      return this.transactions.filter(searchTerm => {
        return (
          searchTerm.sequenceID
            .toLowerCase()
            .indexOf(this.search.toLowerCase()) !== -1
        );
      });
    },
    headers() {
      return [
        {
          text: "Customer Name",
          value: "customerId",
          filterable: true,
          sortable: false
        },
        {
          text: "Acc. #",
          value: "accountNumber",
          filterable: true,
          sortable: false
        },
        { text: "Susu", value: "susu" },
        { text: "Shares Capital", value: "shares" },
        { text: "Member Savings", value: "savings" },
        { text: "Kids & Kitty", value: "kidsKitty" },
        { text: "Special Deposit", value: "specialDeposit" },
        { text: "Loan (Repayment)", value: "loans" }
      ];
    },
    dialog() {
      return this.$store.getters["getDialogState"]("add");
    }
  },
  methods: {
    open(payload) {
      this.$store.dispatch("updateDialog", payload, { root: true });
    },
    uploadFile(file) {
      this.$store.dispatch("balances/uploadBalances", file);
    }
  },
  watch: {
    search: debounce(function(searchTerm) {
      if (searchTerm !== "") {
        const query = `?searchTerm=${searchTerm}&page=1&size=30`;
        this.$store.dispatch("balances/searchList", query);
      } else {
        this.$store.dispatch("balances/list", "?page=1&size=30");
      }
    }, 800),
    options: {
      handler(value) {
        const { page, itemsPerPage } = value;
        const query = `?page=${page}&size=${itemsPerPage}`;
        this.$store.dispatch("balances/list", query);
      },
      deep: true
    }
  },
  created() {
    const query = `?page=1&size=30`;
    this.$store.dispatch("balances/list", query);
  }
};
</script>

<style scoped>
.toolbar__search {
  max-width: 440px;
  width: 100%;
}
.toolbar__refresh {
  color: var(--muted-foreground);
}
.add-btn {
  border-radius: var(--radius) !important;
  height: 40px !important;
  font-weight: 600;
}
.cell-strong {
  font-weight: 600;
  color: var(--foreground);
}
</style>
